import { InfoCircleOutlined } from '@ant-design/icons'
import { navigate } from '@reach/router'
import { Input, Row, Button, Form, Col, Tooltip, Modal } from 'antd'
import moment from 'moment'
import React, { createContext, useContext, useReducer, useState } from 'react'
import styled from 'styled-components'
import { ROOT_PATH } from '../common/config'
import { encryptPhone } from '../common/helper'
import { useLoading, useMedia, useSMS } from '../common/hooks'
import { passwordRule, required, validateMessages } from '../common/validate'
import { forgotPwdSendSMS, forgotPwdSMSVerify, forgotPwdUpdate, forgotUsernameVerify } from '../services'
import { ErrorBanner } from './ui'

const StyledForgotForm = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;

  form {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .ant-form-item {
    width: 60%;
  }

  .button-groups {
    margin-top: auto;
    align-self: flex-end;
    width: 100%;
    margin-bottom: 78px;

    .ant-btn {
      width: 120px !important;
    }
  }
  h1 p {
    font-weight: normal;
    margin-top: 1em;
    font-size: 16px;
    margin-bottom: -1em;
  }
`

enum Step {
  Username,
  OTP,
  RESET,
}

interface ForgotState {
  step: Step
  // 忘记账号请求流水号
  fpId: string
  username: string
  reset: boolean
}

type ForgotAction = { type: 'ChangeStep'; payload: Step } | { type: 'ApplyForm'; payload: Partial<ForgotState> }
export const ForgotPasswordContext = createContext({ state: {} as ForgotState, dispatch: {} as React.Dispatch<ForgotAction> })

const useForgot = (initial?: Partial<ForgotState>) => {
  const initialState: ForgotState = {
    step: Step.Username,
    fpId: '',
    username: '',
    reset: false,
  }
  const reducer = (state: ForgotState, action: ForgotAction): ForgotState => {
    switch (action.type) {
      case 'ChangeStep':
        return { ...state, step: action.payload }
      case 'ApplyForm':
        return { ...state, ...action.payload }
      default:
        return state
    }
  }
  const [state, dispatch] = useReducer(reducer, Object.assign({}, initialState, initial))
  return {
    state,
    dispatch,
  }
}
export const ForgotPasswordProvider: React.FC<{ username?: string }> = (props) => {
  const { state, dispatch } = useForgot(props.username ? { username: props.username, step: Step.OTP, reset: true } : undefined)
  return <ForgotPasswordContext.Provider value={{ state, dispatch }}>{props.children}</ForgotPasswordContext.Provider>
}

const ButtonGroup: React.FC<{ loading?: boolean; last?: boolean; errMsg?: string }> = (props) => {
  const { state, dispatch } = useContext(ForgotPasswordContext)
  const back = () => {
    if (state.step === Step.Username) navigate(ROOT_PATH)
    else dispatch({ type: 'ChangeStep', payload: state.step - 1 })
  }

  return (
    <>
      <Row align="middle" justify="space-between" className="button-groups">
        {props.last ? (
          <Button size="large" onClick={() => navigate(ROOT_PATH)}>
            Cancel
          </Button>
        ) : (
          <Button size="large" onClick={back}>
            Back
          </Button>
        )}
        <Button size="large" type="primary" htmlType="submit" loading={props.loading}>
          {props.last ? 'Confirm' : 'Continue'}
        </Button>
      </Row>
      {props.errMsg && <ErrorBanner>{props.errMsg}</ErrorBanner>}
    </>
  )
}

const Username: React.FC = () => {
  const { dispatch } = useContext(ForgotPasswordContext)
  const { loading, start, finish } = useLoading()
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined)
  const isMobile = useMedia('(max-width: 600px)')

  const onFinish = async (values: { username: string }) => {
    start()
    setErrMsg(undefined)
    try {
      await forgotUsernameVerify(values.username)
      finish()
      dispatch({ type: 'ApplyForm', payload: { username: values.username } })
      dispatch({ type: 'ChangeStep', payload: Step.OTP })
      setErrMsg(undefined)
    } catch (error) {
      setErrMsg(error.message)
      finish()
    }
  }
  return (
    <>
      <Form layout={isMobile ? 'horizontal' : 'vertical'} requiredMark={false} validateMessages={{ required: 'please enter your username' }} onFinish={onFinish}>
        <h1>Forgotten Password</h1>
        <Form.Item label="Enter Your User Name" name="username" rules={[required]}>
          <Input size="large" />
        </Form.Item>
        <ButtonGroup loading={loading} errMsg={errMsg} />
      </Form>
    </>
  )
}

const OTP: React.FC = () => {
  const { dispatch, state } = useContext(ForgotPasswordContext)
  const [tel, setTel] = useState('')
  const { loading, start, finish } = useLoading()
  const { sending, sendSMS, remaining, smsSended } = useSMS(
    () => forgotPwdSendSMS({ usrId: state.username }),
    ({ fpId, tel }) => {
      setTel(tel)
      dispatch({ type: 'ApplyForm', payload: { fpId } })
    }
  )
  const isMobile = useMedia('(max-width: 600px)')
  const onFinish = async (values: { sms: string }) => {
    start()
    try {
      await forgotPwdSMSVerify({ sms: values.sms, fpId: state.fpId })
      finish()
      dispatch({ type: 'ChangeStep', payload: Step.RESET })
    } catch (error) {
      finish()
    }
  }
  return (
    <>
      <Form layout={isMobile ? 'horizontal' : 'vertical'} requiredMark={false} validateMessages={validateMessages} onFinish={onFinish}>
        {state.reset ? <h1>Reset Password</h1> : <h1>Forgotten Password</h1>}
        <Form.Item label="Password OTP" name="sms" rules={[required]} extra={tel ? 'OTP has been sent to your phone: ' + encryptPhone(tel) : undefined}>
          <Row align="middle">
            <Col span="18">
              <Input size="large" />
            </Col>
            <Col span="6">
              <Button type="link" loading={sending} onClick={sendSMS}>
                {smsSended ? `remaining ${moment.utc(remaining * 60 * 1000).format('HH:mm')}` : 'Send OTP'}
              </Button>
            </Col>
          </Row>
        </Form.Item>
        <ButtonGroup loading={loading} />
      </Form>
    </>
  )
}
const RESET: React.FC = () => {
  const { state } = useContext(ForgotPasswordContext)
  const { loading, start, finish } = useLoading()
  const isMobile = useMedia('(max-width: 600px)')

  const onFinish = async ({ password, password2 }: Record<string, string>) => {
    try {
      start()
      await forgotPwdUpdate({ rnpwd: password, npwd: password, fpId: state.fpId })
      finish()
      Modal.success({
        maskClosable: false,
        title: 'Success',
        content: 'Your password reset has been successful !',
        onOk: () => navigate(ROOT_PATH),
      })
    } catch (error) {
      finish()
    }
  }
  return (
    <>
      <Form layout={isMobile ? 'horizontal' : 'vertical'} requiredMark={false} validateMessages={validateMessages} onFinish={onFinish}>
        {state.reset ? <h1>Reset Password</h1> : <h1>Forgotten Password</h1>}
        {state.reset ? null : <h3>Reset Password </h3>}

        <p>You can now reset your new password.</p>
        <Form.Item name="password" label="Enter Your New Password" rules={[{ ...passwordRule, message: 'your new password is not valid' }]}>
          <Row align="middle" gutter={1} justify="space-between">
            <Col span={20}>
              <Input.Password size="large" type="password" />
            </Col>
            <Col span={3}>
              <Tooltip title="Your password should be at least 8 characters long and should contain at least one number, one alphabet and one special characters of !@#%&?$.">
                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
              </Tooltip>
            </Col>
          </Row>
        </Form.Item>
        <Form.Item
          name="password2"
          label="Reenter Your New Password"
          rules={[
            { ...passwordRule, message: 'your new password is not valid' },
            ({ getFieldValue }) => ({
              validator(rule, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve()
                }
                return Promise.reject('The two passwords that you entered do not match!')
              },
              validateTrigger: 'blur',
            }),
          ]}
        >
          <Row align="middle" gutter={1} justify="space-between">
            <Col span={20}>
              <Input.Password size="large" type="password" />
            </Col>
            <Col span={3}>
              {/* <Tooltip title="Your password should be at least 8 alphanumeric character. It should contain at least one alphabet and one digit.">
                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
              </Tooltip> */}
            </Col>
          </Row>
        </Form.Item>
        <ButtonGroup loading={loading} last />
      </Form>
    </>
  )
}

const Steps = {
  [Step.Username]: Username,
  [Step.OTP]: OTP,
  [Step.RESET]: RESET,
}

export const ForgotPassword = () => {
  const { state } = useContext(ForgotPasswordContext)
  const StepForm = Steps[state.step]
  return (
    <StyledForgotForm>
      <StepForm />
    </StyledForgotForm>
  )
}
